import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { BurgerMenuIcon } from "@jti/brand-icons/burger-menu";
import { getMessageBus } from "@jti/fe-message-bus";
import { LanguageSelector } from "../../components/LanguageSelector";
import { SearchHeaderBanner } from "../../components/Search";
import { Container } from "../../eva-components/Container";
import { useUser } from "../../hooks/useUser";
import { Breadcrumbs } from "../Breadcrumbs";
import { Logo } from "../Logo";
import { Navigation } from "./components/Navigation";
import { SignOut } from "./components/SignOut/SignOut";
import { Tools } from "./components/Tools";
import { useBuilderHeaderStyle } from "./hooks/useBuilderHeaderStyle";
import { HeaderProvider } from "./context";
import styles from "./Header.module.scss";
export const Header = ({ isDarkMode = false, logo, isSimplified = false, hideNavigation = false, navLinks, accountLink, accountDropdownSettings, renderMiniCart = null, mobileAppLink, resultsLink, languages, storeLocatorLink, breadcrumbsList, renderSubmenu, overlayHeader = false, withCart = false, onLogOut, }) => {
    const { isUserLoggedIn } = useUser();
    const [headerPortalHeight, setHeaderPortalHeight] = useState(0);
    const [infoBannerHeight, setInfoBannerHeight] = useState(0);
    const headerPortalRef = useCallback((headerPortal) => {
        if (headerPortal === null) {
            return;
        }
        const resizeObserver = new ResizeObserver(() => {
            setHeaderPortalHeight(headerPortal.getBoundingClientRect().height);
        });
        resizeObserver.observe(headerPortal);
    }, []);
    const [navigationOpened, setNavigationOpened] = useState(false);
    const [isSearchOpened, setIsSearchOpened] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isAtTop, setIsAtTop] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const headerRef = useRef(null);
    const headerHeight = headerRef.current?.getBoundingClientRect()?.height || 0;
    const isHidden = !isVisible && overlayHeader && headerHeight + infoBannerHeight - headerPortalHeight < lastScrollY;
    const isTransitionDisabled = headerHeight + infoBannerHeight + 1 > lastScrollY && overlayHeader; // Condition for class to assign transition for element in order to reduce jumping content while scrolling when there is still some navigation in viewport
    const isVisibleOverlay = isVisible && overlayHeader && lastScrollY >= infoBannerHeight;
    const isBuilderStyleSkipped = useBuilderHeaderStyle({
        headerPortalHeight,
        isVisible: (isVisible || isAtTop) && !overlayHeader,
        isVisibleOverlay,
        isTransitionDisabled,
        isOverlayHeader: overlayHeader,
        isHidden,
        styles,
    });
    const messageBus = getMessageBus();
    const shouldAddFixedStyle = isBuilderStyleSkipped && overlayHeader;
    const handleScroll = useCallback(() => {
        const currentScrollY = window.scrollY;
        const isScrollYEqualOrLowerThanZero = currentScrollY <= 0; // Needed to prevent Header disappearing when scrolling up and having "over-scroll bouncing", issue in DESP-82989
        const isVisible = currentScrollY < lastScrollY || isScrollYEqualOrLowerThanZero;
        setIsScrolled(currentScrollY > 0);
        setIsAtTop(isScrollYEqualOrLowerThanZero);
        setIsVisible(isVisible);
        setLastScrollY(currentScrollY);
    }, [lastScrollY]);
    useEffect(() => {
        messageBus.subscribe({
            channel: "infobanner.active",
            callback: ({ payload: { infoBannerHeight } }) => {
                setInfoBannerHeight(infoBannerHeight);
            },
        });
        messageBus.subscribe({
            channel: "infobanner.close",
            callback: () => {
                setInfoBannerHeight(0);
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);
    useEffect(() => {
        if (!isNaN(headerHeight)) {
            document.documentElement.style.setProperty("--header-portal-height", `-${headerHeight - headerPortalHeight}px`);
        }
    }, [headerHeight, headerPortalHeight]);
    useEffect(() => {
        if (navigationOpened) {
            document.body.style.overflow = "hidden";
        }
        else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [navigationOpened]);
    const handleHamburgerClick = () => {
        setNavigationOpened((navigationOpened) => !navigationOpened);
    };
    const openSearch = () => {
        setIsSearchOpened(true);
    };
    const closeSearch = () => {
        setIsSearchOpened(false);
    };
    return (_jsx(HeaderProvider, { children: _jsxs("header", { className: classNames(styles.headerPosition, styles.header, {
                "dark-theme": isDarkMode,
                [styles.darkMode]: isDarkMode,
                [styles.hidden]: isHidden,
                [styles.visible]: (isVisible || isAtTop) && !overlayHeader,
                [styles.transitionDisabled]: isTransitionDisabled,
                [styles.visibleAsOverlay]: isVisibleOverlay,
                [styles.fixed]: shouldAddFixedStyle,
                [styles.transparent]: isAtTop && overlayHeader,
                [styles.withBackground]: isSearchOpened || (isScrolled && overlayHeader),
            }), "data-testid": "header", ref: headerRef, children: [_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.desktopLogo, children: _jsx(Logo, { isDarkMode: isDarkMode, ...logo }) }), _jsxs("div", { className: styles.content, children: [!hideNavigation && !isSimplified && (_jsxs(_Fragment, { children: [_jsx("button", { className: classNames(styles.burgerMenu, {
                                                [styles.hamburgerExpanded]: navigationOpened,
                                            }), onClick: handleHamburgerClick, children: _jsx(BurgerMenuIcon, { className: styles.burgerMenuIcon }) }), _jsx("div", { className: classNames(styles.navigation, { [styles.navigationExpanded]: navigationOpened }), children: _jsxs("div", { className: styles.navigationContent, children: [_jsx(Navigation, { navLinks: navLinks, renderSubmenu: renderSubmenu }), _jsxs("div", { className: styles.navigationBottomSection, children: [_jsx("div", { className: styles.mobileLanguageSelector, "data-testid": "mobileLanguageSelector", children: _jsx(LanguageSelector, { languages: languages, popupContext: "NAVIGATION", variant: "FULL_LANGUAGE_NAME" }) }), isUserLoggedIn && (_jsx(SignOut, { label: accountDropdownSettings?.logoutLabel ?? "", onLogOut: onLogOut }))] })] }) })] })), _jsx("div", { className: styles.mobileLogo, children: _jsx(Logo, { isDarkMode: isDarkMode, ...logo }) }), !isSimplified && (_jsxs("div", { className: styles.tools, "data-testid": "header-tools", children: [_jsx(Tools, { accountLink: accountLink, accountDropdownSettings: accountDropdownSettings, renderMiniCart: renderMiniCart, mobileAppLink: mobileAppLink, storeLocatorLink: storeLocatorLink, resultsLink: resultsLink, onCloseSearch: closeSearch, onOpenSearch: openSearch, isSearchOpened: isSearchOpened, withCart: withCart, onLogOut: onLogOut }), _jsx("div", { className: styles.desktopLanguageSelector, "data-testid": "desktopLanguageSelector", children: _jsx(LanguageSelector, { languages: languages, popupContext: "NAVIGATION", variant: "SHORT_LANGUAGE_NAME" }) })] }))] })] }), _jsx("div", { className: styles.searchLayerContainer, children: _jsx(SearchHeaderBanner, { isOpened: isSearchOpened, onClose: closeSearch, resultsPageUrl: resultsLink, Container: Container }) }), _jsx(Container, { children: _jsx(Breadcrumbs, { list: breadcrumbsList, className: classNames({
                            [styles.breadcrumbsHidden]: navigationOpened,
                        }) }) }), _jsx("div", { className: classNames({
                        [styles.headerPortalHidden]: navigationOpened,
                    }), ref: headerPortalRef, "data-portal": "header" })] }) }));
};
//# sourceMappingURL=Header.js.map